import type { Placement, ReferenceType, Strategy, UseFloatingReturn, UseInteractionsReturn } from '@floating-ui/react';
import {
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  limitShift,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import {
  Fragment,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { HTMLProps, PropsWithChildren, ReactHTMLElement, ReactNode, Ref } from 'react';

import { Styles, css, sva } from '@flipster/styled-system/css';
import { center, hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { checkMobile } from '@flipster/utils/user-agent';

import XMarkIcon from '../icon/generated/XMarkIcon';

export type TooltipOptions = UncontrolledTooltipOptions | ControlledTooltipOptions;
interface BaseTooltipOptions {
  strategy?: Strategy;
  placement?: Placement;
  shape?: TooltipShapeType;
  onOpenChange?: (open: boolean) => void;
  maxWidth?: number;
  availableWidthOffset?: number;
  shiftLimit?: number;
  mainAxisOffset?: number;
  crossAxisOffset?: number;
  // disable arrow middleware and align arrow using placement
  alignArrow?: boolean;
  // enable flip middleware to auto-flip placement when space is insufficient
  enableFlip?: boolean;
}
interface UncontrolledTooltipOptions extends BaseTooltipOptions {
  mobileAlert?: () => void;
  open?: never;
}
interface ControlledTooltipOptions extends BaseTooltipOptions {
  open: boolean;
}

interface UseTooltipReturn extends UseFloatingReturn<ReferenceType>, UseInteractionsReturn {
  arrowRef: Ref<HTMLDivElement | null>;
  open: boolean;
  shape: TooltipShapeType;
  alignArrow: boolean;
}
export function useTooltip({
  strategy = 'absolute',
  placement = 'top',
  shape = 'white',
  onOpenChange,
  maxWidth = 200,
  availableWidthOffset = 0,
  shiftLimit = 16,
  mainAxisOffset = 12,
  crossAxisOffset = 0,
  alignArrow = false,
  enableFlip = false,
  ...props
}: TooltipOptions): UseTooltipReturn {
  const onOpenChangeCallback = useRef<((open: boolean) => void) | undefined>(onOpenChange);
  const arrowRef = useRef<HTMLDivElement>(null);

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);

  const controlledOpen = 'open' in props ? props.open : null;
  const open = controlledOpen ?? uncontrolledOpen;
  const mobileAlert = 'mobileAlert' in props ? props.mobileAlert : undefined;

  const isUncontrolled = controlledOpen == null;

  onOpenChangeCallback.current = onOpenChange;
  useEffect(() => {
    if (controlledOpen) onOpenChangeCallback.current?.(controlledOpen);
  }, [controlledOpen]);

  const data = useFloating({
    strategy,
    placement,
    open,
    onOpenChange(o: boolean) {
      onOpenChange?.(o);
      if (checkMobile() && mobileAlert) {
        if (o) mobileAlert();
        return;
      }
      setUncontrolledOpen(o);
    },
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({ mainAxis: mainAxisOffset, crossAxis: crossAxisOffset }),
      ...(enableFlip ? [flip({ padding: 10 })] : []),
      shift({
        padding: 10,
        limiter: limitShift({
          offset: shiftLimit,
        }),
      }),
      size({
        apply({ elements, availableWidth }) {
          // oxlint-disable-next-line no-param-reassign
          elements.floating.style.maxWidth = `${Math.min(availableWidth - availableWidthOffset, maxWidth)}px`;
        },
      }),
      arrow((state) => {
        const padding = Math.min(
          shape === 'promotion' ? PROMOTION_TOOLTIP_ROUNDED : BASE_TOOLTIP_ROUNDED,
          Math.min(state.rects.floating.width, state.rects.floating.height) / 2,
        );
        return { element: arrowRef, padding };
      }),
    ],
  });

  const { context } = data;

  const role = useRole(context, { role: 'tooltip' });
  const hover = useHover(context, { move: false, enabled: isUncontrolled });
  const focus = useFocus(context, { enabled: isUncontrolled });
  const dismiss = useDismiss(context);
  const interactions = useInteractions([hover, focus, dismiss, role]);

  return useMemo(
    () => ({
      open,
      ...interactions,
      ...data,
      arrowRef,
      shape,
      alignArrow,
    }),
    [open, interactions, data, shape, alignArrow],
  );
}

type ContextType = ReturnType<typeof useTooltip> | null;
const TooltipContext = createContext<ContextType>(null);
export const useTooltipContext = () => {
  const context = useContext(TooltipContext);
  if (context == null) throw new Error('Tooltip components must be wrapped in <Tooltip />');
  return context;
};

export const TooltipBase = ({ children, ...options }: { children: ReactNode } & TooltipOptions) => {
  const tooltip = useTooltip(options);
  return <TooltipContext value={tooltip}>{children}</TooltipContext>;
};

interface TooltipTriggerProps {
  ref?: Ref<HTMLElement>;
  asChild?: boolean;
  css?: Styles;
}
const TooltipTrigger = ({
  ref: propRef,
  children,
  asChild = false,
  css: cssProps,
}: PropsWithChildren<TooltipTriggerProps>) => {
  const context = useTooltipContext();
  const childrenRef = (children as ReactHTMLElement<HTMLElement>).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  if (asChild && isValidElement(children)) {
    return cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...(children.props as HTMLProps<HTMLElement>),
        // @ts-expect-error data bindings has no type definitions in HTMLProps
        'data-state': context.open ? 'open' : 'closed',
      }),
    );
  }

  return (
    <div
      ref={ref}
      // style the trigger based on the state
      data-state={context.open ? 'open' : 'closed'}
      className={css(cssProps)}
    >
      {children}
    </div>
  );
};

interface TooltipContentType extends HTMLProps<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
  portal?: true;
  zIndex?: number;
  css?: Styles;
  onClose?(): void;
}
const TooltipContent = ({
  ref: propRef,
  shape,
  css: cssProps,
  zIndex,
  children,
  onClose,
  portal,
  ...props
}: TooltipContentType) => {
  const context = useTooltipContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);
  const recipeCssRaw = tooltipRecipe.raw({
    shape: context.shape,
    placement: context.placement,
    clickable: Boolean(props.onClick),
  });

  if (!context.open) return null;

  const Container = portal ? FloatingPortal : Fragment;

  function getArrowPositions() {
    if (!context.alignArrow) {
      return {
        left: context.middlewareData.arrow?.x,
        top: context.middlewareData.arrow?.y,
      };
    }
    if (!context.elements.floating) return;
    const padding = Math.min(
      shape === 'promotion' ? PROMOTION_TOOLTIP_ROUNDED : BASE_TOOLTIP_ROUNDED,
      Math.min(context.elements.floating.clientWidth, context.elements.floating.clientHeight) / 2,
    );
    switch (context.placement) {
      case 'top-start':
      case 'bottom-start':
        return { left: padding };
      case 'left-start':
      case 'right-start':
        return { top: padding };
      case 'top-end':
      case 'bottom-end':
        return { right: -padding };
      case 'left-end':
      case 'right-end':
        return { bottom: -padding };
    }
  }

  return (
    <Container>
      <div
        ref={ref}
        className={css(recipeCssRaw.tooltip, cssProps)}
        style={{
          ...context.floatingStyles,
          zIndex: zIndex ?? (portal ? token('zIndex.tooltip') : 1),
        }}
        {...context.getFloatingProps(props)}
      >
        <div ref={context.arrowRef} className={css(recipeCssRaw.arrow)} style={getArrowPositions()} />
        <div className={css(recipeCssRaw.content)}>{children}</div>
        {shape !== 'promotion' && onClose && (
          <span className={center()}>
            <wbr />
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className={css(recipeCssRaw['close-button'])}
            >
              <XMarkIcon size={16} />
            </button>
          </span>
        )}
      </div>
    </Container>
  );
};

export const Tooltip = Object.assign(TooltipBase, {
  Trigger: TooltipTrigger,
  Content: TooltipContent,
});

export const Placements = [
  'top',
  'right',
  'bottom',
  'left',
  'top-start',
  'top-end',
  'bottom-start',
  'bottom-end',
  'left-start',
  'left-end',
  'right-start',
  'right-end',
] as const;

const BASE_TOOLTIP_ROUNDED = 8;
const PROMOTION_TOOLTIP_ROUNDED = 999;

type PlacementTypes = (typeof Placements)[number];
const tooltipRecipe = sva({
  slots: ['tooltip', 'arrow', 'content', 'close-button'],
  base: {
    tooltip: {
      textStyle: 'Text/M/Regular',
      py: 12,
      px: 16,
      width: 'max-content',
      rounded: BASE_TOOLTIP_ROUNDED,
      ...hstack.raw({ alignItems: 'flex-start' }),
    },
    arrow: {
      pos: 'absolute',
      pointerEvents: 'none',
      w: 0,
      h: 0,
      border: '8px solid transparent',
    },
    'close-button': {
      buttonShape: 'inline-icon',
      ml: 12,
    },
  },
  variants: {
    shape: {
      white: {
        tooltip: {
          bgColor: 'inverse.white',
          color: 'inverse.black',
        },
        arrow: {
          '--arrow-color': '{colors.inverse.white}',
        },
        'close-button': {
          color: 'content.default.level-1',
        },
      },
      gray: {
        tooltip: {
          bgColor: 'background.default.level-4',
          color: 'content.default.level-5',
        },
        arrow: {
          '--arrow-color': '{colors.background.default.level-4}',
        },
        'close-button': {
          color: 'content.default.level-2',
        },
      },
      promotion: {
        tooltip: {
          textStyle: 'Text/XXS/Regular',
          py: 6,
          px: 10,
          bgGradient: 'programs.promotion',
          color: 'inverse.white',
          rounded: PROMOTION_TOOLTIP_ROUNDED,
        },
        arrow: {},
      },
    },
    clickable: {
      true: {
        tooltip: {
          cursor: 'pointer',
        },
      },
    },
    placement: Placements.reduce((acc, cur) => ({ ...acc, [cur]: {} }), {} as Record<PlacementTypes, {}>),
  },
  compoundVariants: [
    {
      placement: ['top', 'top-start', 'top-end'],
      css: {
        arrow: {
          top: '100%',
          left: '50%',
          borderTopColor: 'var(--arrow-color)',
        },
      },
    },
    {
      placement: ['right', 'right-start', 'right-end'],
      css: {
        arrow: {
          top: '50%',
          right: '100%',
          borderRightColor: 'var(--arrow-color)',
        },
      },
    },
    {
      placement: ['bottom', 'bottom-start', 'bottom-end'],
      css: {
        arrow: {
          bottom: '100%',
          left: '50%',
          borderBottomColor: 'var(--arrow-color)',
        },
      },
    },
    {
      placement: ['left', 'left-start', 'left-end'],
      css: {
        arrow: {
          top: '50%',
          left: '100%',
          borderLeftColor: 'var(--arrow-color)',
        },
      },
    },
    {
      shape: 'promotion',
      placement: ['top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end'],
      css: {
        arrow: {
          borderX: '5px solid transparent',
          borderY: '7px solid transparent',
          '--arrow-color': '{colors.gradient.purple.level-0}',
        },
      },
    },
    {
      shape: 'promotion',
      placement: ['right', 'right-start', 'right-end'],
      css: {
        arrow: {
          right: 'calc(100% - 2px)',
          borderX: '7px solid transparent',
          borderY: '5px solid transparent',
          '--arrow-color': '{colors.gradient.purple.level-0}',
        },
      },
    },
    {
      shape: 'promotion',
      placement: ['left', 'left-start', 'left-end'],
      css: {
        arrow: {
          left: 'calc(100% - 2px)',
          borderX: '7px solid transparent',
          borderY: '5px solid transparent',
          '--arrow-color': '{colors.gradient.purple.level-2}',
        },
      },
    },
  ],
});

export type TooltipShapeType = (typeof tooltipRecipe.variantMap.shape)[number];
